/**
 * Shared helpers for FutureBot page-tool hooks (dashboard / my deals /
 * analytics). Pure utilities — no React.
 */

export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Wait for a table/page refetch to settle so tool results can report fresh
 * counts. `isLoadingRef.current` must always hold the live loading boolean.
 *
 * The refetch usually starts on the next render tick after a setState, so we
 * first give it a grace window to START, then wait for it to finish.
 */
export async function waitForIdle(isLoadingRef, { startGraceMs = 800, timeoutMs = 8000 } = {}) {
  const startedAt = Date.now();
  while (!isLoadingRef.current && Date.now() - startedAt < startGraceMs) {
    await sleep(80);
  }
  while (isLoadingRef.current && Date.now() - startedAt < timeoutMs) {
    await sleep(120);
  }
  // Let the post-fetch state (totalCount etc.) commit.
  await sleep(60);
}

/** Human-readable one-liner for a filter condition array. */
export function summarizeConditions(filters) {
  if (!Array.isArray(filters) || filters.length === 0) return 'none';
  return filters
    .map((f) => {
      if (!f || !f.field) return 'invalid';
      const value = Array.isArray(f.value)
        ? `[${f.value.join(', ')}]`
        : f.value !== undefined && f.value !== null && f.value !== ''
          ? String(f.value)
          : '';
      return `${f.field} ${f.operator}${value ? ` ${value}` : ''}`;
    })
    .join('; ');
}

/**
 * Resolve a smartlist by id (preferred) or exact name (case-insensitive).
 * Returns { list } or { error }.
 */
export function resolveSmartList(lists, { smartListId, name, targetName } = {}) {
  const all = Array.isArray(lists) ? lists : [];
  if (smartListId) {
    const list = all.find((l) => l.id === smartListId);
    return list
      ? { list }
      : { error: `No smartlist with id "${smartListId}". Call list_smart_lists for current ids.` };
  }
  const wanted = (name || targetName || '').trim().toLowerCase();
  if (!wanted) return { error: 'Provide smartListId or the exact smartlist name.' };
  const matches = all.filter((l) => (l.name || '').trim().toLowerCase() === wanted);
  if (matches.length === 0) {
    return { error: `No smartlist named "${name || targetName}". Call list_smart_lists to see what exists.` };
  }
  if (matches.length > 1) {
    return { error: `${matches.length} smartlists are named "${name || targetName}" — use smartListId instead (see list_smart_lists).` };
  }
  return { list: matches[0] };
}

/** Compact smartlist shape for tool results / reference data. */
export function describeSmartList(list, currentUserId) {
  if (!list) return null;
  return {
    id: list.id,
    name: list.name,
    isGlobal: !!list.isGlobal,
    isDefault: !!list.isDefault,
    createdByMe: !!currentUserId && list.createdBy === currentUserId,
    filterCount: Array.isArray(list.filters) ? list.filters.length : 0,
    filters: summarizeConditions(list.filters),
  };
}

/**
 * Permission gate for editing/deleting a smartlist: owners always may; global
 * lists may additionally be managed by CRM admins.
 */
export function canManageSmartList(list, { userId, isCRMAdmin }) {
  if (!list) return false;
  if (list.createdBy === userId) return true;
  if (list.isGlobal && isCRMAdmin) return true;
  return false;
}
